import { useEffect, type ComponentType } from "react";
import { Navigate, Outlet, useLocation, useParams, type LoaderFunction, type RouteObject } from "react-router";
import { canonicalHost, isExploreSelectionChange } from "../ssr/policy";
import { pageMetadata } from "../ssr/metadata";
import { syncRouteMetadata } from "../ssr/syncRouteMetadata";
import { usePageData } from "../ssr/usePageData";
import NotFoundPage from "../pages/NotFoundPage";
import { AppLayout } from "../components/layout/AppLayout";
import DiscoverPage from "../pages/DiscoverPage";
import { mergeAttributionSearch, track, trackPageView } from "../lib/analytics";
import { Spinner } from "../components/ui/Spinner";
import { clearDynamicImportReload, prepareDynamicImportReload } from "../lib/chunkRecovery";

// Every route except the homepage is code-split: on client navigation the router
// keeps the current page visible while the chunk loads; on a direct hit the
// hydrateFallbackElement spinner below shows until the page module resolves.
// During a rolling deploy, an already-open tab can still reference a removed old hashed chunk.
// Reload once to fetch the current index/asset graph; the session marker prevents reload loops.
function page(load: () => Promise<{ default: ComponentType }>) {
	return async () => {
		try {
			const module = await load();
			if (typeof window !== "undefined") clearDynamicImportReload(window.sessionStorage);
			return { Component: module.default };
		} catch (error) {
			if (typeof window !== "undefined" && prepareDynamicImportReload(error, window.sessionStorage)) {
				window.location.reload();
				// Keep React Router's default error page from flashing while navigation reloads.
				return await new Promise<never>(() => undefined);
			}
			throw error;
		}
	};
}

function AnalyticsLayout() {
	const location = useLocation();
	const data = usePageData();

	useEffect(() => {
		trackPageView(location.pathname);
	}, [location.pathname]);

	useEffect(() => {
		const host = canonicalHost(window.location.host);
		syncRouteMetadata(pageMetadata(`${window.location.protocol}//${host}`, location.pathname, data));
	}, [location.pathname, data]);

	if (data?.status === 404) return <NotFoundPage />;
	return <Outlet />;
}

/**
 * Forwards the app's `/open/*` deep-link shape (share.muhaha.ai/open/game|drama/…) to the web
 * player route, preserving the id param — <Navigate> can't interpolate params. Without these, a
 * shared playable link hit the catch-all and silently became the homepage / 404'd.
 *
 * The query rides along: share links carry `l_data` + `utm_*`, and dropping them here loses the
 * attribution for everything the visitor does after the redirect.
 */
function OpenRedirect({ to }: { to: (param: string) => string }) {
	const params = useParams();
	const { search } = useLocation();
	const value = params.id;
	const target = value ? mergeAttributionSearch(to(value), search) : "/";

	useEffect(() => {
		if (value) track("deeplink_redirect", { target, source: "open_route" });
	}, [target, value]);

	return <Navigate to={target} replace />;
}

/**
 * Two-segment variant of OpenRedirect for the private co-play room link
 * (share.muhaha.ai/open/room/:id/:code → /play/:id?playroomCode=:code, the same query the DM
 * room-invite card navigates to). Separate from OpenRedirect because that one collapses to a single
 * param; here both segments are load-bearing and the code has to survive as a query param.
 */
function OpenRoomRedirect() {
	const { id, code } = useParams();
	const { search } = useLocation();
	const target =
		id && code
			? mergeAttributionSearch(
					`/play/${encodeURIComponent(id)}?playroomCode=${encodeURIComponent(code)}`,
					search,
				)
			: "/";

	useEffect(() => {
		if (id && code) track("deeplink_redirect", { target, source: "open_route" });
	}, [target, id, code]);

	return <Navigate to={target} replace />;
}

export function createRoutes(loader: LoaderFunction): RouteObject[] { return [
	{
		id: "page",
		loader,
		shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate, formMethod }) =>
			!formMethod && isExploreSelectionChange(currentUrl, nextUrl) ? false : currentUrl.pathname !== nextUrl.pathname || defaultShouldRevalidate,
		element: <AnalyticsLayout />,
		hydrateFallbackElement: (
			<div className="grid h-[100dvh] place-items-center bg-bg text-muted">
				<Spinner size={26} />
			</div>
		),
		children: [
			{ path: "/store-download", lazy: page(() => import("../pages/StoreDownloadPage")) },
			{ path: "/tos", lazy: page(() => import("../pages/TermsPage")) },
			{ path: "/privacy", lazy: page(() => import("../pages/PrivacyPage")) },
			{ path: "/login", lazy: page(() => import("../pages/LoginPage")) },
			{ path: "/support/login", lazy: page(() => import("../pages/LoginSupportPage")) },
			// 13+ notice + birthday correction. Outside <AppLayout> on purpose: an age-blocked
			// session 401s every call the layout's chrome makes (wallet, friends, DMs, the realtime
			// stream), so the one page that exists to fix that must not sit inside it.
			{ path: "/age-restriction", lazy: page(() => import("../pages/AgeRestrictionPage")) },
			// Web-platform unified logout (hive docs/sso.md §6) is NOT here: /logout is a server
			// route now (server/routes/auth-session.ts), same as its /auth/acl counterpart.
			// JavaScript cannot clear an HttpOnly session cookie, so the page that used to do it
			// could only have pretended to — and it made the editor's logout redirect pull down
			// the entire front-end bundle to run one fetch.
			{
				element: <AppLayout />,
				children: [
					{ path: "/", element: <DiscoverPage /> },
					{ path: "/all/tags", element: <DiscoverPage /> },
					{ path: "/about", lazy: page(() => import("../pages/AboutPage")) },
					// Topic landing page: the same feed, filtered — see web/lib/topics.ts.
					{ path: "/t/:tag", element: <DiscoverPage /> },
					{ path: "/search", lazy: page(() => import("../pages/SearchPage")) },
					{ path: "/messages", lazy: page(() => import("../pages/MessagesPage")) },
					{ path: "/messages/:id", lazy: page(() => import("../pages/DirectConversationPage")) },
					{ path: "/play/:id", lazy: page(() => import("../pages/PlayablePlayer")) },
					{ path: "/create", lazy: page(() => import("../pages/CreatePage")) },
					{ path: "/library", lazy: page(() => import("../pages/LibraryRedirectPage")) },
					{ path: "/friends", element: <Navigate to="/messages?tab=interactions" replace /> },
					{ path: "/wallet", lazy: page(() => import("../pages/WalletPage")) },
					{ path: "/gdpr/radd", lazy: page(() => import("../pages/GdprRaddPage")) },
					{ path: "/notifications", element: <Navigate to="/messages?tab=notifications" replace /> },
					{ path: "/settings", lazy: page(() => import("../pages/SettingsPage")) },
					{ path: "/settings/cosmetics", lazy: page(() => import("../pages/CosmeticsPage")) },
					{ path: "/u/:handle", lazy: page(() => import("../pages/ProfilePage")) },
					// Dramas are playables; playableKind.ts links to /drama/:id but the route was missing.
					{ path: "/drama/:id", lazy: page(() => import("../pages/PlayablePlayer")) },
					// App share/invite deep links (share.muhaha.ai/open/*) → the matching web route.
					{ path: "/open/game/:id", element: <OpenRedirect to={(id) => `/play/${id}`} /> },
					{ path: "/open/drama/:id", element: <OpenRedirect to={(id) => `/drama/${id}`} /> },
					{ path: "/open/room/:id/:code", element: <OpenRoomRedirect /> },
					// The invite deep link gets a real landing page (state + sign-up CTA), not a
					// redirect: the code is read from the path, so no query merging is needed.
					{ path: "/open/invite/:code", lazy: page(() => import("../pages/InviteLandingPage")) },
					// A dead link is a dead link: bouncing it to the homepage told visitors (and
					// crawlers) that every typo'd URL is a valid page.
					{ path: "*", element: <NotFoundPage /> },
				],
			},
		],
	},
]; }
